import { useState } from 'react';
import {
  AlertTriangle,
  Check,
  CloudOff,
  GitMerge,
  RefreshCw,
  RotateCcw,
  UploadCloud,
} from 'lucide-react';
import { useSyncState } from '../../sync/useSync.js';
import { resetAndResync, syncNow } from '../../sync/engine.js';
import { formatRelative, pluralise } from '../../lib/format.js';
import { cn } from '../../lib/cn.js';
import { Button } from '../ui/button.js';
import { Dialog } from '../ui/dialog.js';
import { Badge } from '../ui/primitives.js';

/**
 * The trust indicator.
 *
 * In an offline-capable app the single most important question a user has is
 * "is my work safe?" - so this is always visible, never behind a menu, and it
 * answers with a state and a count rather than a spinner.
 */
export function SyncIndicator({ compact }: { compact?: boolean }) {
  const state = useSyncState();
  const [open, setOpen] = useState(false);
  const [resetting, setResetting] = useState(false);

  const busy = state.phase === 'pulling' || state.phase === 'pushing' || state.phase === 'bootstrapping';
  const attention = state.failed > 0 || state.conflicts > 0 || state.phase === 'error';

  const { icon, label, tone } = describe(state, busy, attention);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`Sync status: ${label}`}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-lg border px-2 py-1 text-xs font-medium transition-colors',
          tone === 'danger' && 'border-danger/30 bg-danger-subtle text-danger',
          tone === 'warning' && 'border-warning/30 bg-warning-subtle text-warning',
          tone === 'muted' && 'border-border bg-surface-sunken text-muted-foreground',
          tone === 'success' && 'border-border bg-surface-sunken text-muted-foreground',
        )}
      >
        <span className={cn(busy && 'animate-spin')}>{icon}</span>
        {!compact ? <span>{label}</span> : null}
        {state.pending > 0 ? (
          <span className="rounded bg-foreground/10 px-1 tabular">{state.pending}</span>
        ) : null}
      </button>

      <Dialog
        open={open}
        onOpenChange={setOpen}
        title="Sync status"
        description="MouldTrack stores everything on this device first, then syncs."
        size="sm"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => {
                setResetting(true);
                void resetAndResync().finally(() => setResetting(false));
              }}
              loading={resetting}
            >
              <RotateCcw className="size-4" /> Re-download all
            </Button>
            <Button variant="primary" onClick={() => void syncNow()} disabled={!state.online}>
              <RefreshCw className="size-4" /> Sync now
            </Button>
          </>
        }
      >
        <dl className="divide-y divide-border text-sm">
          <Row label="Connection">
            {state.online ? (
              <Badge tone="success">Online</Badge>
            ) : (
              <Badge tone="warning">Offline - changes are queued</Badge>
            )}
          </Row>
          <Row label="Last synced">{formatRelative(state.lastPullAt)}</Row>
          <Row label="Waiting to send">
            {state.pending === 0 ? (
              <span className="text-muted-foreground">Nothing - everything is saved</span>
            ) : (
              <Badge tone="info">{pluralise(state.pending, 'change')}</Badge>
            )}
          </Row>
          {state.failed > 0 ? (
            <Row label="Rejected">
              <Badge tone="danger">{pluralise(state.failed, 'change')}</Badge>
            </Row>
          ) : null}
          {state.conflicts > 0 ? (
            <Row label="Conflicts">
              <Badge tone="warning">{pluralise(state.conflicts, 'row')}</Badge>
            </Row>
          ) : null}
          {state.lastError ? (
            <Row label="Last error">
              <span className="text-danger">{state.lastError}</span>
            </Row>
          ) : null}
        </dl>

        <p className="mt-4 rounded-lg bg-surface-sunken px-3 py-2.5 text-xs text-muted-foreground">
          You can keep working with no connection. Everything you change is saved on this device and
          sent automatically the moment you are back online.
        </p>
      </Dialog>
    </>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 py-2.5">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right font-medium">{children}</dd>
    </div>
  );
}

function describe(
  state: ReturnType<typeof useSyncState>,
  busy: boolean,
  attention: boolean,
): { icon: React.ReactNode; label: string; tone: 'success' | 'muted' | 'warning' | 'danger' } {
  if (!state.online) {
    return { icon: <CloudOff className="size-3.5" />, label: 'Offline', tone: 'warning' };
  }
  if (state.failed > 0) {
    return { icon: <AlertTriangle className="size-3.5" />, label: 'Needs attention', tone: 'danger' };
  }
  if (state.conflicts > 0) {
    return { icon: <GitMerge className="size-3.5" />, label: 'Conflicts', tone: 'warning' };
  }
  if (busy) {
    return { icon: <RefreshCw className="size-3.5" />, label: 'Syncing', tone: 'muted' };
  }
  if (attention) {
    return { icon: <AlertTriangle className="size-3.5" />, label: 'Sync error', tone: 'danger' };
  }
  if (state.pending > 0) {
    return { icon: <UploadCloud className="size-3.5" />, label: 'Sending', tone: 'muted' };
  }
  return { icon: <Check className="size-3.5" />, label: 'Saved', tone: 'success' };
}
